<template>
    <div id="project-overview">
        <div class="vx-row">
            <div class="vx-col lg:w-2/3 w-full">
                <div class="vx-row match-height">
                    <div class="vx-col w-full lg:w-1/3">
                        <div class="vx-card p-6">
                            <h4>{{ $t('tasks.describeProject') }}</h4>
                            <div class="vx-row">
                                <div class="vx-col w-full">
                                    <vue-apex-charts ref="chartCompletedPercentage" type="radialBar"
                                                     :options="describeProject.chartOptions"
                                                     :series="describeProject.series"></vue-apex-charts>
                                </div>
                                <div class="vx-col w-full -mt-4">
                                    <vs-divider></vs-divider>
                                    <span class="statistical-title font-semibold">{{ $t('tasks.startDate') }}: </span>
                                    <span>{{ projectDetails.startTimeCvt }}</span>
                                </div>
                                <div class="vx-col w-full mt-2">
                                    <span class="statistical-title font-semibold">{{ $t('tasks.deadline') }}: </span>
                                    <span>{{ projectDetails.endTimeCvt }}</span>
                                </div>
                                <div class="vx-col w-full mt-2">
                                    <span class="statistical-title font-semibold">{{ $t('tasks.teamMembers') }}: </span>
                                    <span>{{ projectDetails.employeeCount }}</span>
                                </div>
                                <div class="vx-col w-full mt-2" v-if="projectDetails.type">
                                    <vs-divider>
                                        <span class="statistical-title font-semibold">{{
                                                $t('tasks.projectType')
                                            }}: </span>
                                        <span>{{ projectDetails.type }}</span>
                                    </vs-divider>
                                </div>
                                <div class="vx-col w-full">
                                    <vs-divider></vs-divider>
                                    <span class="statistical-title font-semibold">{{
                                            $t('tasks.personInCharge')
                                        }}</span>
                                    <div class="items-center flex">
                                        <div>
                                            <img :src="projectDetails.responsibleEmployee.avatar" alt=""
                                                 class="w-12 h-12 rounded-full mr-3 mt-3"
                                                 v-if="projectDetails.responsibleEmployee && projectDetails.responsibleEmployee.avatar">
                                            <img
                                                src="@/assets/images/portrait/small/user-avatar.png"
                                                alt="" class="w-12 h-12 rounded-full mr-3 mt-3" v-else>
                                        </div>
                                        <div>
                                            <h6>
                                                {{
                                                    projectDetails.responsibleEmployee ?
                                                        projectDetails.responsibleEmployee.fullName : ''
                                                }}
                                            </h6>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="vx-col w-full lg:w-2/3">
                        <div class="vx-card p-6 lg:mt-0 mt-6">
                            <h4>{{ $t('tasks.employeeProgress') }}</h4>
                            <div class="vx-row mt-6">
                                <div class="vx-col xl:w-1/2 w-full">
                                    <v-select class="w-full" v-model="selectedEmployee" :clearable="false"
                                              :options="employeeOption" :reduce="(options) => options.value"
                                              @input="changeSelectEmployee"
                                    >
                                    </v-select>
                                </div>
                                <div class="vx-col xl:w-1/2 w-full xl:mt-0 mt-2">
                                    <date-range-picker ref="picker" class="w-full"
                                                       :locale-data="{ format: 'dd/mm/yyyy' }"
                                                       :opens="'left'" @update="dateRangePicker" v-model="dateRange">
                                    </date-range-picker>
                                </div>

                            </div>
                            <div class="vx-row mt-6">
                                <div class="vx-col w-1/2"><a
                                    class="statistical-title font-semibold">{{ $t('tasks.totalTask') }} ({{
                                        totalTask
                                    }})</a></div>
                                <div class="vx-col w-1/2">
                                    <vs-progress :percent="totalTaskPercent" color="dark"
                                                 :title="totalTaskTitle"></vs-progress>
                                </div>
                            </div>
                            <div class="vx-row mt-6">
                                <div class="vx-col w-1/2"><a
                                    class="statistical-title font-semibold">{{ $t('tasks.inProcessTask') }}
                                    ({{ countInProcessTask }})</a></div>
                                <div class="vx-col w-1/2">
                                    <vs-progress :percent="inProcessTask" color="rgb(1, 119, 253)"
                                                 :title="totalInProcessTaskTaskTitle">
                                    </vs-progress>
                                </div>
                            </div>
                            <div class="vx-row mt-6">
                                <div class="vx-col w-1/2"><a
                                    class="statistical-title font-semibold">{{ $t('tasks.overdueTask') }}
                                    ({{ overdueTask }})</a></div>
                                <div class="vx-col w-1/2">
                                    <vs-progress :percent="overdueTask" color="danger" :title="totalOverdueTaskTitle">
                                    </vs-progress>
                                </div>
                            </div>
                            <div class="vx-row mt-6">
                                <div class="vx-col w-1/2"><a
                                    class="statistical-title font-semibold">{{ $t('tasks.onTimeTask') }}
                                    ({{ countCompleteTask }})</a></div>
                                <div class="vx-col w-1/2">
                                    <vs-progress :percent="completeTask" color="success"
                                                 :title="totalCompleteTaskTitle">
                                    </vs-progress>
                                </div>
                            </div>
                            <div class="vx-row mt-6">
                                <div class="vx-col w-1/2"><a
                                    class="statistical-title font-semibold">{{ $t('tasks.cancelTask') }}
                                    ({{ countCancelTask }})</a></div>
                                <div class="vx-col w-1/2">
                                    <vs-progress :percent="cancelTask" color="warning" :title="totalCancelTaskTitle">
                                    </vs-progress>
                                </div>
                            </div>

                        </div>
                    </div>
                    <div class="vx-col w-full">
                        <div class="vx-card p-6 mt-6">
                            <h4>{{ $t('tasks.todayWork') }}</h4>
                            <div class="w-full mt-8" v-if="projectData.length > 0">
                                <vs-divider></vs-divider>
                                <VuePerfectScrollbar ref="scrollBar" @ps-scroll-y="handleScroll"
                                                     class="scroll-area-project p-0" :settings="settings"
                                                     :key="$vs.rtl">
                                    <div class="today-work p-4 mt-4" v-for="(job, index) in projectData"
                                         :key="index">
                                        <div class="vx-row">
                                            <div class="vx-col lg:w-1/4 md:w-1/2 w-full">
                                                <p class="title-overview font-semibold">{{
                                                        $t('tasks.startDate')
                                                    }}</p>
                                                <p class="description">{{ job.startTimeStr }}</p>
                                            </div>
                                            <div class="vx-col lg:w-1/4 md:w-1/2 w-full">
                                                <p class="title-overview font-semibold">{{ $t('tasks.title') }}</p>
                                                <vx-tooltip class="flex items-center" :text="job.title">
                                                    <p class="description"
                                                       style="text-overflow: ellipsis ; overflow: hidden; white-space: nowrap;">
                                                        {{ job.title }}</p>
                                                </vx-tooltip>
                                            </div>
                                            <div class="vx-col lg:w-1/4 md:w-1/2 w-full lg:mt-0 mt-2">
                                                <p class="title-overview font-semibold">{{ $t('tasks.status') }}</p>
                                                <!--                                            <p class="description">{{ status(job.status) }}</p>-->
                                                <vs-chip :color="chipColorStatus(job.status)">
                                                    <span>{{ stringStatus(job.status) }}</span>
                                                </vs-chip>
                                            </div>
                                            <div class="vx-col lg:w-1/4 md:w-1/2 w-full lg:mt-0 mt-2">
                                                <p class="title-overview font-semibold">{{
                                                        $t('tasks.assignedPerson')
                                                    }}</p>
                                                <p class="description">{{ job.assigneeName }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </VuePerfectScrollbar>
                            </div>
                            <div class="w-full mt-4 text-center" v-else>
                                <img src="../../../assets/images/pages/no_data.png" alt=""
                                     style="width: 38%; height: auto">
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="vx-col lg:w-1/3 w-full lg:mt-0 mt-6">
                <div class="vx-card p-6">
                    <h4>{{ $t('tasks.projectStatistics') }}</h4>
                    <div class="flex statistical justify-between items-center">
                        <div class="flex items-center">
                            <feather-icon class="icon-item mr-2" icon="ClockIcon"></feather-icon>
                            <p class="statistical-title font-semibold">{{ $t('tasks.timeRemaining') }}</p>
                        </div>
                        <span class="text-danger font-semibold">{{ statisticData.dateRemain }} ngày</span>
                    </div>
                    <div class="flex statistical justify-between items-center">
                        <div class="flex items-center">
                            <feather-icon class="icon-item mr-2" icon="CheckCircleIcon"></feather-icon>
                            <p class="statistical-title font-semibold">{{ $t('tasks.taskCreated') }} </p>
                        </div>
                        <span class="text-warning font-semibold">{{ statisticData.total }}</span>
                    </div>
                    <div class="flex statistical justify-between items-center">
                        <div class="flex items-center">
                            <feather-icon class="icon-item mr-2" icon="PlayCircleIcon"></feather-icon>
                            <p class="statistical-title font-semibold">{{
                                    $t('tasks.theMissionIsUnderway')
                                }} </p>
                        </div>
                        <span class="font-semibold" style="color: #0177fd">{{ countProgress }}</span>
                    </div>
                    <div class="flex statistical justify-between items-center">
                        <div class="flex items-center">
                            <feather-icon class="icon-item mr-2" icon="CalendarIcon"></feather-icon>
                            <p class="statistical-title font-semibold">{{ $t('tasks.outOfDate') }} </p>
                        </div>
                        <span class="text-danger font-semibold">{{ statisticData.countLate }} </span>
                    </div>
                    <div class="statistical">
                        <div class="flex justify-between items-center">
                            <div class="flex items-center">
                                <feather-icon class="icon-item mr-2" icon="PercentIcon"></feather-icon>
                                <p class="statistical-title font-semibold">{{
                                        $t('tasks.onTimeCompletionRate')
                                    }} </p>
                            </div>
                            <span class="text-primary">{{ statisticData.completedPercentage }} %</span>
                        </div>
                        <vs-progress :percent="statisticData.completedPercentage"
                                     color="primary"></vs-progress>
                    </div>
                </div>
                <div class="vx-card p-6 mt-6">
                    <h4>{{ $t('tasks.recentActivity') }}</h4>
                    <VuePerfectScrollbar class="scroll-area--task-log mt-4" :settings="settings"
                                         :key="$vs.rtl"
                                         ref="scrollBar" @ps-scroll-y="handleScroll" v-if="projectActivity.length > 0">
                        <div v-for="(data, ind) in projectActivity " :key="ind">
                            <vs-divider></vs-divider>
                            <div class="flex gap-2 mt-6">
                                <svg xmlns="http://www.w3.org/2000/svg" width="23" height="24"
                                     viewBox="0 0 23 24"
                                     fill="none">
                                    <rect x="2.94628" y="11.9706" width="11.8333" height="11.8333"
                                          transform="rotate(-45 2.94628 11.9706)"
                                          :stroke=" data.type === 1 ? 'rgba(var(--vs-primary), 1)' : 'rgba(var(--vs-warning), 1)'"
                                          stroke-width="1.5"/>
                                </svg>
                                <div class="w-full">
                                    <div class="flex">
                                        <p class="font-semibold" v-if="data.type === 1">{{
                                                data.metaData.createdBy
                                            }}</p>
                                        <p class="font-semibold" v-if="data.type === 2">{{ data.createdBy }}</p>
                                        <p v-if="data.type === 1" class="ml-4">{{
                                                data.startTimeAct
                                            }}</p>
                                    </div>
                                    <div class="flex items-center mt-1">
                                        <vs-chip :color="chipColor(data.type)" v-if="data.type">{{
                                                data.type === 1 ?
                                                    'Thêm mới' : 'Cập nhật'
                                            }}
                                        </vs-chip>
                                        <p class="ml-4 font-semibold" style="width: 70%">
                                            {{ data.metaData.title }}
                                        </p>
                                    </div>
                                    <div>
                                        <p v-if="data.type === 2 && data.metaData.newTitle">Title:
                                            <span class="mr-2 text-danger line-through">
                                                        <span>{{ data.metaData.oldTitle }}</span>
                                                    </span>
                                            <span class="text-primary">{{
                                                    data.metaData.newTitle
                                                }}</span></p>
                                        <p v-if="data.type === 2 && data.oldStartTimeAct !== data.newEndTimeAct">
                                            Start Time:
                                            <span class="mr-2 text-danger line-through">
                                                        <span>{{ data.oldStartTimeAct }}</span>
                                                    </span>
                                            - <span class="text-primary">{{ data.newStartTimeAct }}</span></p>
                                        <p v-if="data.type === 2 && data.oldEndTimeAct !== data.newEndTimeAct">
                                            End
                                            Time:
                                            <span class="mr-2 text-danger line-through">
                                                        <span>{{ data.oldEndTimeAct }}</span>
                                                    </span>
                                            -
                                            <span class="text-primary">{{ data.newEndTimeAct }}</span>
                                        </p>
                                        <p v-if="data.type === 2 && data.metaData.newPriority">
                                            Priority:
                                            <del style="margin-right: 5px;"> {{
                                                    data.metaData.oldPriority
                                                }}
                                            </del>
                                            <span style="color:#00b393">{{ data.metaData.newPriority }}
                                        </span>
                                        </p>
                                        <p v-if="data.type === 2 && data.metaData.newStatus">Status:
                                            <span class="mr-2 text-danger line-through">
                                                        <span class="status-color" :title="data.metaData.oldStatus">{{
                                                                data.metaData.oldStatus
                                                            }}</span>
                                                    </span>
                                            <span class="status-color" :title="data.metaData.newStatus">{{
                                                    data.metaData.newStatus
                                                }}</span>
                                        </p>
                                        <p v-if="data.type === 2 && data.metaData.newAssignee">
                                            Assignee:
                                            <del style="margin-right: 5px;"> {{
                                                    data.metaData.oldAssignee
                                                }}
                                            </del>
                                            <span style="color:#00b393">{{
                                                    data.metaData.newAssignee
                                                }}</span></p>
                                        <p v-if="data.type === 2 && data.metaData.newCollaborators">
                                            Collaborators:
                                            <del style="margin-right: 5px;"> {{
                                                    data.metaData.oldCollaborators
                                                }}
                                            </del>
                                            {{
                                                data.metaData.newCollaborators
                                            }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </VuePerfectScrollbar>
                    <div class="w-full mt-4 text-center" v-else>
                        <img src="../../../assets/images/pages/no_data.png" alt="" style="width: 60%; height: auto">
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment"
import vSelect from 'vue-select'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import DateRangePicker from '../../../components/vue2-daterange-picker/components/DateRangePicker.vue'
import {vi} from 'vuejs-datepicker/dist/locale'
import VueApexCharts from 'vue-apexcharts'

export default {
    name: "ProjectOverview",
    components: {
        VuePerfectScrollbar,
        vSelect,
        DateRangePicker,
        VueApexCharts
    },

    props: {
        projectDetails: {}
    },

    data() {
        return {
            vi: vi,
            projectData: [],
            projectDataTime: [],
            statusOptions: [],
            projectId: null,
            countProgress: 0,
            projectActivity: [],
            statisticData: [],
            statistical: [],
            dataSta: [],
            total_doing_count: [],
            metaData: null,
            settings: {
                maxScrollbarLength: 60,
                wheelSpeed: .60,
            },
            dateRange: {
                startDate: new Date(new Date().setHours(0, 0, 0, 0)),
                endDate: new Date(new Date().setHours(23, 59, 59, 999))
            },
            page: 0,
            pageSize: 10,
            disableScrollbar: false,
            startDateAddRange: {
                endDate: new Date(new Date().setDate(new Date().getDate() - new Date().getDay() + 5)),
                startDate: new Date(new Date().setDate(new Date().getDate() - new Date().getDay() + 1))
            },

            selectedEmployee: null,
            projectDataPopup: [],
            employeeOption: [],
            employeeOptionPopup: [],

            totalTask: 0,
            totalTaskTitle: 0,
            totalTaskPercent: 100,

            inProcessTask: 0,
            overdueTask: 0,
            onTimeTask: 0,
            completeTask: 0,
            cancelTask: 0,

            countInProcessTask: 0,
            countOverdueTask: 0,
            countCompleteTask: 0,
            countCancelTask: 0,

            totalInProcessTaskTaskTitle: 0,
            totalOverdueTaskTitle: 0,
            totalCompleteTaskTitle: 0,
            totalCancelTaskTitle: 0,

            describeProject: {
                series: [],
                chartOptions: {
                    chart: {
                        type: 'radialBar',
                        offsetY: -20,
                        sparkline: {
                            enabled: true
                        }
                    },
                    plotOptions: {
                        radialBar: {
                            startAngle: -90,
                            endAngle: 90,
                            track: {
                                background: "#e7e7e7",
                                strokeWidth: '97%',
                                margin: 5, // margin is in pixels
                                dropShadow: {
                                    enabled: true,
                                    top: 2,
                                    left: 0,
                                    color: '#999',
                                    opacity: 1,
                                    blur: 2
                                }
                            },
                            dataLabels: {
                                value: {
                                    offsetY: -40,
                                },
                                style: {
                                    colors: ['rgba(var(--vs-primary), 1)']
                                }
                            }
                        }
                    },
                    grid: {
                        padding: {
                            top: -10
                        }
                    },
                    fill: {
                        // type: 'gradient',
                        // gradient: {
                        //     shade: 'light',
                        //     shadeIntensity: 0.4,
                        //     inverseColors: false,
                        //     opacityFrom: 1,
                        //     opacityTo: 1,
                        //     stops: [0, 50, 53, 91]
                        // },
                        colors: ['rgba(var(--vs-primary), 1)']
                    },
                    labels: ['Tiến độ'],
                },
            }
        }
    },

    async created() {
        this.projectId = parseInt(this.$route.query.id, 10);
        let branchList = JSON.parse(localStorage.getItem("branchInfoList") || "[]");
        if (branchList.length === 0) {
            return this.$vs.notify({
                text: this.$t("warningMenuOrganization.emptyEmployee"),
                iconPack: "feather",
                icon: "icon-alert-circle",
                color: "danger",
            });
        }
        this.organizationBranchOptions = branchList.filter(el => el.organizationBranchType !== 3).map((item) => {
            return {value: item.id, label: item.organizationBranchName};
        });
        this.organizationBranchOptions.unshift({value: null, label: this.$t("menuOrganization.all")});
        await this.getStt();
        await this.getRecentActivity(false);
        await this.getStatistic();
        await this.getTaskDetail();
        await this.getEmployee();
        await this.changeSelectEmployee();
        setTimeout(() => {
            let percent = this.projectDetails.completedPercentage ? this.projectDetails.completedPercentage : 0
            this.describeProject.series = [percent]
        }, 300)
    },

    methods: {

        getEmployee() {
            return new Promise((resolve, reject) => {
                let id = this.projectId;
                this.$vs.loading()
                this.$crm.get(`/project/get-project-employees/${id ? id : ''}`).then((response) => {
                    if (response.data && response.data.length > 0) {
                        this.employeeOption = response.data.map(el => {
                            return {
                                value: el.id,
                                label: el.fullName,
                            }
                        })
                        this.employeeOption.unshift({value: 0, label: 'Tất cả'});
                        this.selectedEmployee = 0;
                    } else {
                        if (!this.popupActiveUse) {
                            this.employeeOption = [{value: 0, label: 'Tất cả'}]
                        } else {
                            this.employeeOptionPopup = []
                        }
                    }
                    resolve(response)
                    this.$vs.loading.close()
                }).catch(err => {
                    reject(err)
                    this.$vs.loading.close();
                    return this.$vs.notify({
                        title: 'Thông báo',
                        text: 'Vui lòng chọn thanh vien',
                        color: 'danger',
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                    })
                })
            })
        },

        getTaskDetail() {
            return new Promise((resolve, reject) => {
                let body = {
                    projectId: this.projectId,
                    startTime: this.dateRange.startDate,
                    endTime: this.dateRange.endDate,

                }
                this.$vs.loading();
                this.$crm.post(`/task/find-by-condition`, body).then((response) => {
                    if (response.data.content && response.data.content.length > 0) {
                        this.projectData = response.data.content.map(t => {
                            t.startTimeStr = moment(t.startTime).format('DD/MM/YYYY HH:mm')
                            return t;
                        })
                    } else {
                        this.projectData = []
                    }
                    this.$vs.loading.close();
                    resolve(response)
                }).catch(err => {
                    reject(err)
                    this.$vs.loading.close();
                    return this.$vs.notify({
                        title: 'Lỗi',
                        text: err.response.data.message || err.response.data.error_description || err.response.data.error,
                        color: 'danger',
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                    });
                })
            })
        },

        getStatistic() {
            return new Promise((resolve, reject) => {
                let body = {
                    projectId: this.projectId,

                }
                this.$vs.loading();
                this.$crm.post(`/task/statistic-chart`, body).then((response) => {
                    if (response.data) {
                        this.statisticData = response.data;
                        this.statisticData.detailDTOs.forEach(task => {
                            if (task.isDoing === 1) {
                                this.countProgress += task.count;
                            }
                        });
                        this.statisticData.countLate = this.statisticData.detailDTOs.find(el => el.isLate).count
                        this.statisticData.completedPercentage = this.statisticData.completedPercentage ? parseInt(this.statisticData.completedPercentage.toFixed(0), 10) : 0
                    }
                    this.$vs.loading.close();
                    resolve(response)
                }).catch(err => {
                    reject(err)
                    this.$vs.loading.close();
                    return this.$vs.notify({
                        title: 'Lỗi',
                        text: err.response.data.message || err.response.data.error_description || err.response.data.error,
                        color: 'danger',
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                    });
                })
            })
        },

        getRecentActivity(isScroll) {
            return new Promise((resolve, reject) => {
                let body = {
                    page: this.page,
                    pageSize: this.pageSize,
                    projectId: this.projectId,
                    startTime: this.startDateAddRange.startDate.toISOString(),
                    endTime: this.startDateAddRange.endDate.toISOString(),
                }

                this.$vs.loading();
                this.$crm.post(`/task/log-history/find-by-condition`, body).then((response) => {
                    this.$vs.loading.close();
                    if (response.data.content && response.data.content.length > 0) {
                        if (isScroll) {
                            let data = response.data.content.map(el => {
                                el.metaData = JSON.parse(el.metaData);
                                return el;
                            })
                            this.projectActivity.push(...data)
                        } else {
                            this.projectActivity = response.data.content.map(el => {
                                el.metaData = JSON.parse(el.metaData);
                                return el;
                            })
                        }
                    } else {
                        this.projectActivity = []
                    }
                    this.disableScrollbar = this.projectActivity.length < response.data.totalElements
                    resolve(response)
                }).catch(err => {
                    reject(err)
                    this.$vs.loading.close();
                    return this.$vs.notify({
                        title: 'Lỗi',
                        text: err.response.data.message || err.response.data.error_description || err.response.data.error,
                        color: 'danger',
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                    });
                })
            })
        },

        getStt() {
            return new Promise((resolve, reject) => {
                this.$vs.loading();
                this.$crm.get(`/setting/organization-task-status/find-by-organization-id`).then((response) => {
                    this.$vs.loading.close();
                    if (response.data && response.data.length > 0) {
                        this.statusOptions = response.data.map((el) => {
                            return {
                                value: el.status,
                                label: el.title,
                            };
                        });
                    }
                    resolve(response)
                }).catch(err => {
                    reject(err)
                    this.$vs.loading.close();
                    return this.$vs.notify({
                        title: 'Lỗi',
                        text: err.response.data.message || err.response.data.error_description || err.response.data.error,
                        color: 'danger',
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                    });
                })
            })
        },

        changeSelectEmployee() {
            return new Promise((resolve, reject) => {
                let body = {
                    assigneeId: this.selectedEmployee ? this.selectedEmployee : null,
                    projectId: this.projectId,
                    startTime: this.dateRange.startDate,
                    endTime: this.dateRange.endDate,

                }
                this.$vs.loading();
                this.$crm.post(`/task/find-by-condition`, body).then((response) => {
                    if (response.data.content && response.data.content.length > 0) {
                        this.totalTask = response.data.content.length;
                        if (this.totalTask > 0) {
                            this.totalTaskPercent = 100
                        }
                        this.totalTaskTitle = `Tổng số nhiệm vụ là : ${this.totalTask} nhiệm vụ`;

                        this.countInProcessTask = response.data.content.filter(task => task.status === 1).length;
                        this.inProcessTask = (this.countInProcessTask / this.totalTask) * 100;
                        this.totalInProcessTaskTaskTitle = `Tổng số nhiệm vụ đang làm là : ${response.data.content.filter(task => task.status === 1).length} nhiệm vụ`;

                        this.overdueTask = response.data.content.filter(task => task.late === true).length;
                        this.totalOverdueTaskTitle = `Tổng số nhiệm vụ quá hạn là : ${this.overdueTask} nhiệm vụ`;

                        this.countCompleteTask = response.data.content.filter(task => task.status === 2).length;
                        this.completeTask = (this.countCompleteTask / this.totalTask) * 100;
                        this.totalCompleteTaskTitle = `Tổng số nhiệm vụ hoàn thành là : ${response.data.content.filter(task => task.status === 2).length} nhiệm vụ`;

                        this.countCancelTask = response.data.content.filter(task => task.status === 3).length;
                        this.cancelTask = (this.countCancelTask / this.totalTask) * 100;
                        this.totalCancelTaskTitle = `Tổng số nhiệm vụ đã hủy là : ${response.data.content.filter(task => task.status === 3).length} nhiệm vụ`;
                    } else {
                        this.countCancelTask = 0;
                        this.countCompleteTask = 0;
                        this.overdueTask = 0;
                        this.countInProcessTask = 0;
                        this.inProcessTask = 0;
                        this.totalTask = 0;
                        this.totalTaskPercent = 0;
                        this.completeTask = 0;
                        this.cancelTask = 0;

                        this.totalTaskTitle = `Tổng số nhiệm vụ là : 0 nhiệm vụ`;
                        this.totalInProcessTaskTaskTitle = `Tổng số nhiệm vụ đang làm là : 0 nhiệm vụ`;
                        this.totalOverdueTaskTitle = `Tổng số nhiệm vụ quá hạn là : 0 nhiệm vụ`;
                        this.totalCompleteTaskTitle = `Tổng số nhiệm vụ hoàn thành là : 0 nhiệm vụ`;
                        this.totalCancelTaskTitle = `Tổng số nhiệm vụ đã hủy là : 0 nhiệm vụ`;
                    }
                    this.$vs.loading.close();
                    resolve(response)
                }).catch(err => {
                    reject(err)
                    this.$vs.loading.close();
                    return this.$vs.notify({
                        title: 'Lỗi',
                        text: err.response.data.message || err.response.data.error_description || err.response.data.error,
                        color: 'danger',
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                    });
                })
            })
        },

        dateRangePicker() {
            this.dateRange.startDate = new Date(this.dateRange.startDate)
            this.dateRange.endDate = new Date(this.dateRange.endDate)
            if (this.dateRange.startDate && this.dateRange.endDate) {
                this.changeSelectEmployee()
            }
        },

        handleScroll(evt) {
            const {scrollTop, offsetHeight, scrollHeight} = evt.target
            if (((scrollTop + offsetHeight) >= scrollHeight) && this.disableScrollbar) {
                this.page = this.page + 1;
                this.getRecentActivity(true);
            }
        }
    },

    computed: {
        status() {
            return (status) => {
                let obj = this.statusOptions.find((x) => x.value == status)
                if (!obj) return
                return obj.label;
            };
        },

        chipColor() {
            return (value) => {
                switch (value) {
                    case 1:
                        return "primary";
                    case 2:
                        return "warning";
                }
            };
        },

        chipColorStatus() {
            return (value) => {
                switch (value) {
                    case 0:
                        return "rgba(var(--vs-warning), 1)";
                    case 1:
                        return "rgba(1, 119, 253, 1)";
                    case 2:
                        return "rgba(var(--vs-success), 1)";
                    case 3:
                        return "rgba(var(--vs-danger), 1)";
                }
            }
        },

        stringStatus() {
            return (value) => {
                switch (value) {
                    case 0:
                        return "To do";
                    case 1:
                        return "Doing";
                    case 2:
                        return "Completed";
                    case 3:
                        return "Canceled";
                }
            }
        },
    }
}
</script>

<style lang="scss">
.statistical {
    margin-top: 16px;
    padding: 16px;
    align-items: center;
    gap: 12px;
    border-radius: 5px;
    border: 1px dashed;
}

.statistical-title {
    color: #1b1a17;
}

.icon-item {
    width: 24px;
    height: 24px;

}

.navbar-item-content {
    /*color: var(--Light-text-secondary, #89898A);*/
    font-size: 12px;
    font-weight: 400;
}

.scroll-area-project {
    width: 100%;
    min-height: 200px;
    height: calc(var(--vh, 1vh) * 100 - 254px);

    .today-work {
        border: 1px dashed;
        border-radius: 0.5rem;
    }
}

.header-title {
    /*color: var(--Light-text-main, #1B1A17);*/
    font-size: 20px;
    font-weight: 700;
    text-transform: capitalize;
    margin-bottom: 15px;
}

del {
    text-decoration: line-through;
}

.title-overview {
    color: #1b1a17;
}

.description {
    color: #89898A;
}

.scroll-area--task-log {
    min-height: 200px;
    height: calc(var(--vh, 1vh) * 100 - 440px);
}

.status-color[title~="To"] {
    color: rgba(var(--vs-warning), 1);
}

.status-color[title~="Doing"] {
    color: rgba(1, 119, 253, 1);
}

.status-color[title~="Completed"] {
    color: rgba(var(--vs-success), 1);
}

.status-color[title~="Canceled"] {
    color: rgba(var(--vs-danger), 1);
}
</style>
